import { FUNCTIONS, REVIEW_STEP, type Issue } from '@upm/domain';
import { Banner, Button, cx } from '../../ui';
import { answerSections } from '../answerSections';
import { useIntake } from '../IntakeContext';
import { rowText } from '../logic';
import { StepHeader } from './common';

const fnLabel = (path: string) => FUNCTIONS.find((f) => `tools.${f.id}` === path)?.label ?? path;

/** What still blocks submit in one section (errors from validateForSubmit), said as what to do. */
function neededText(step: number, items: Issue[]): string {
  if (step !== 2) return items.map((i) => i.message).join(' ');
  const rows = items.filter((i) => i.code === 'required').map((i) => `“${fnLabel(i.path)}”`);
  const rest = items.filter((i) => i.code !== 'required').map((i) => i.message);
  const list = rows.length <= 1 ? rows.join('') : `${rows.slice(0, -1).join(', ')} and ${rows[rows.length - 1]}`;
  const rowText = rows.length === 0 ? null
    : `${list} ${rows.length === 1 ? 'has' : 'have'} no answer yet. Pick the tools you use, or choose Not applicable if your firm doesn’t do this.`;
  return [rowText, ...rest].filter(Boolean).join(' ');
}

/** `attempted`: the firm pressed Submit and the server said answers are missing. */
export function StepReview({ onEdit, attempted }: { onEdit: (step: number) => void; attempted: boolean }) {
  const { answers: a, tools, catalogLabels, issues } = useIntake();
  const sections = answerSections(a, (fn) => rowText(tools[fn], catalogLabels));

  // Needed before submit: the live submit errors, by section. Optional: every other unanswered row
  // that isn't optional by design.
  const errors = issues.filter((i) => i.severity === 'error');
  const needed = sections
    .map((sec) => ({ sec, n: errors.filter((i) => i.step === sec.step).length, text: neededText(sec.step, errors.filter((i) => i.step === sec.step)) }))
    .filter((x) => x.text);
  const neededCount = needed.reduce((t, x) => t + x.n, 0);
  const waiting = sections
    .map((sec) => ({ title: sec.title, n: sec.rows.filter((row) => !row.v && !row.required && !row.optional).length })).filter((x) => x.n > 0);
  const waitCount = waiting.reduce((t, x) => t + x.n, 0);
  const waitText = waitCount === 0 ? null
    : `${waitCount === 1 ? '1' : waitCount}${needed.length ? ' other' : ''} ${waitCount === 1 ? 'question is' : 'questions are'} still blank (${waiting.map((x) => `${x.n} in ${x.title}`).join(', ')}). `
      + (waitCount === 1 ? 'You can submit without it, and we’ll go over it on the call.' : 'You can submit without them, and we’ll go over them on the call.');

  return (
    <div className="flex flex-col gap-5">
      <StepHeader step={REVIEW_STEP} title="Review and submit">Check your answers. You can still change anything on the call.</StepHeader>
      {needed.length > 0 && (
        <Banner tone={attempted ? 'error' : 'warn'} role={attempted ? 'alert' : undefined}>
          <div className="font-semibold">{neededCount === 1 ? '1 answer' : `${neededCount} answers`} needed before you can submit</div>
          <ul className="m-0 mt-1 flex list-none flex-col gap-2.5 p-0">
            {needed.map(({ sec, text }) => (
              <li key={sec.step} className="flex flex-col items-start gap-1.5">
                <span>In <span className="font-semibold">{sec.title}</span>: {text}</span>
                <Button size="sm" variant="outline" onClick={() => onEdit(sec.step)}>Answer it in {sec.title}</Button>
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {waitText && <Banner tone="info"><span className="font-semibold">Optional:</span> {waitText}</Banner>}
      {sections.map((sec) => (
        <section key={sec.title} className="overflow-hidden rounded-card border border-line bg-white" aria-labelledby={`rev-${sec.step}`}>
          <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5 py-3">
            <h2 id={`rev-${sec.step}`} className="m-0 text-base font-semibold">{sec.title}</h2>
            <button type="button" onClick={() => onEdit(sec.step)} aria-label={`Edit ${sec.title}`} className="-mr-2 min-h-11 rounded-lg border-0 bg-transparent px-3 text-sm font-semibold text-brand hover:bg-brand-tint">Edit</button>
          </div>
          <dl className="m-0 px-5 pb-2 pt-1">
            {sec.rows.map((row) => (
              <div key={row.k} className="flex flex-wrap gap-x-4 gap-y-0.5 border-b border-line-soft py-2.5 last:border-b-0">
                <dt className="flex-[0_1_260px] text-sm text-muted">{row.k}</dt>
                <dd className={cx('m-0 flex-[1_1_280px] text-sm [overflow-wrap:anywhere]', row.v ? 'font-medium text-ink' : row.required ? 'font-semibold text-bad' : row.optional ? 'text-muted' : 'font-semibold text-warn-fg')}>
                  {row.v || (row.required ? 'Not answered · needed to submit' : row.optional ? 'Nothing added' : 'Not answered')}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      <div className="rounded-card border border-line bg-white px-5 py-4 text-sm text-ink-2">
        <div className="mb-1.5 text-base font-semibold text-ink">What happens after you submit</div>
        <ol className="m-0 flex list-decimal flex-col gap-1 pl-5 marker:font-semibold marker:text-brand">
          <li>You pick a time for your Discovery Session. It’s the next screen.</li>
          <li>In the session we go through your answers together and walk you through how the move works, from setup to go-live.</li>
          <li>Next we send the agreement to sign and let you know which files to share. You upload them from your status page.</li>
        </ol>
      </div>
    </div>
  );
}
